export type WABAMigration = {
  id: number;
  ownerEmail: string;
  partnerEmail: string;
  existingWABAId: string;
  existingWABAName: string;
  newWABAId: string;
  newWABAName: string;
  businessManagerId: string;
  phoneNumbers: string[];
  businessVerificationStatus: string;
  WABAReviewStatus: string;
  twoFADisabled: boolean;
  krWABACreated: boolean;
  clientConfirm: boolean;
  readyForMigration: boolean;
  migrationInitiated: boolean;
  migrationConfirmed: boolean;
  createdAt: string;
  updatedAt: string;
};

export type InfoApiUsage = {
  partner: Partner;
  apiUsage: DataCache[];
};

export type Partner = {
  id: number;
  timezone: string;
  user?: User;
  isActivated: boolean;
};

export enum CreditLineState {
  SHARED,
  MANUALLY_REVOKED,
  AUTO_REVOKED,
  NONE,
}

export type User = {
  id: number;
  email: string;
  isAdmin: boolean;
  createdAt: string;
  updatedAt: string;
  partner?: Partner;
  status: string;
  uplineEmail: string;
  isActive: boolean;
};

export enum UserStatusEnum {
  NotInitiated = 'NotInitiated',
  Initiated = 'Initiated',
  Completed = 'Completed',
}

export type Account = {
  id: number;
  timezone: string;
  userId: number;
  wabaId: string;
  name: string;
  businessName: string;
  businessId: string;
  currency: string;
  status: string;
  createdAt: string;
  phoneNumbers: PhoneNumber[];
  ownerEmail: string;
  manager?: Manager;
  user?: User;
};

export type Manager = {
  id: number;
  partnerId?: number;
  partner?: Partner;
  creditLineAllocationConfigId: null | string;
  creditLineState: CreditLineState;
  partnerKey?: PartnerKey;
};

export type NewPhonesListType = (PhoneNumber & { rowSpan: number })[];

export type PhoneNumber = {
  id: number;
  accountId: number;
  value: string;
  certAvailableAt: string;
  certLastCheckedAt: string;
  createdAt: string;
  limit: string;
  updatedAt: string;
  nameStatus: string;
  qualityRating: string;
  verifiedName: string;
  phoneNumberId: string;
  status: string;
  provisionServer?: ProvisionServer;
  account: Account;
  cloudApiWebhookUrl: string;
};

export type Me = {
  email: string;
  id: number;
  isAdmin: boolean;
  partnerId: number;
  status: string;
};

export type PartnerKey = {
  id: number;
  value: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string;
  usedAt: string;
  usage: number;
  serviceProvider?: ServiceProvider;
};

export type Usage = {
  wabaId: string;
  phoneNumbers: {
    id: number;
    accountId: number;
    value: string;
    certLastCheckedAt: string;
    certAvailableAt: string;
    createdAt: string;
    updatedAt: string;
  }[];
  email: string;
  partnerEmail: string;
  wabaName: string;

  totalCost: number;
  FREE_TIER: number;
  FREE_ENTRY: number;
  PAID_BUSINESS: number;
  PAID_USER: number;
  dividedDataEachDates: ConversationData[];
};

export type PricingAnalytics = {
  wabaId: string;
  fetchedTime: string;
  allMessages: {
    messagesSent: number;
    messagesDelivered: number;
    messagesReceived: number;
  };
  allDeliveries: {
    total: number;
    marketing: number;
    utility: number;
    authentication: number;
    authenticationInternational: number;
    service: number;
  };
  freeDeliveries: {
    total: number;
    freeCustomerService: number;
    freeEntryPoint: number;
  };
  paidDeliveries: {
    total: number;
    marketing: number;
    utility: number;
    authentication: number;
    authenticationInternational: number;
  };
  approximateCharges: {
    total: number;
    marketing: number;
    utility: number;
    authentication: number;
    authenticationInternational: number;
  };
};

export type ConversationData = {
  end: number;
  start: number;
  FREE_TIER: number;
  FREE_ENTRY: number;
  PAID_BUSINESS: number;
  PAID_USER: number;
  cost: number;
};

export type Audit = {
  id: number;
  action: string;
  payload: string;
  timestamp: string;
};

export type PhoneCertInfo = Pick<PhoneNumber, 'id' | 'value'>;

export type ApiKey = {
  isActive: boolean;
  value: string;
};

export type ServiceProvidersOnPartners = {
  partner: Partner;
  partnerId: number;
  serviceProvider: ServiceProvider;
  serviceProviderId: number;
  assignedAt: Date;
  assignedBy: string;
};

export type ServiceProvider = {
  id: number;
  name: string;
  options: any;
  partners: ServiceProvidersOnPartners[];
  createdAt: Date;
  updateAt: Date;
  partnerKeys: PartnerKey;
  provisionServers: ProvisionServer;
  isActive: boolean;
};

export type ProvisionServer = {
  id: number;
  name: string;
  options: any;
  serviceProviderId: number;
  phoneNumberId: number;
  serviceProvider: ServiceProvider;
  phoneNumber: PhoneNumber;
};

export type DataCache = {
  key: string;
  total: string;
};

export type CloudApiMigration = {
  id: number;
  details: string;
  isSuccess: boolean;
  requester: CloudApiMigrationRequester;
  phoneNumber: PhoneNumber;
  createdAt: string;
  updatedAt: string;
  createdBy: User;
  ownerEmail: string;
  partnerEmail: string;
  WABAId: string;
  WABAName: string;
};

export enum CloudApiMigrationRequester {
  DASHBOARD,
  PARTNER_API,
}

export type CloudApiRegisterDTO = {
  pin: string;
  backup?: OnPremiseBackupType;
};

export type OnPremiseBackupType = {
  password: string;
  data: string;
};